import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useWindowSize } from 'react-use';
import { useTheme } from '@emotion/react';
import { v4 as uuidv4 } from 'uuid';
import { cs } from '@bbkAdminUtils/css-utils';
import { FunnelContext } from '@bbkFunnel/components/FunnelContext';
import {
  EventSource,
  type EventObject,
  type ExecuteEventsHandler,
} from '@bbkFunnel/events/events';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import { PricingCard } from '@bbkPricingPage/components/card/pricing-card';
import CurrencyDropdown from '@bbkPricingPage/components/dropdown/currency-dropdown';
import { EqualHeightContextProvider } from '@bbkPricingPage/components/equal-height-component/equal-height-context';
import PeriodSwitchBox from '@bbkPricingPage/components/switch-box/period-switch-box';
import { EventKeys } from '@bbkPricingPage/events/defn';
import { sendEvent } from '@bbkPricingPage/events/sender';
import {
  getApplicableFrequency,
  PricingTableHelper,
} from '@bbkPricingPage/helpers/pricing-table-helper';
import {
  getCurrencyFromIso2CountryCode,
  type Iso2CountryCode,
} from '@bbkPricingPage/lib/currencies';
import {
  GroupedBy,
  type Currency,
  type Discount,
  type Frequency,
  type PricingTable as PricingTableType,
  type Subscription,
} from '@bbkPricingPage/types/pricing-table-types';
import { PlanCustomisation } from './components/plan-customisation/plan-customisation';
import {
  getOrCreateVisitorId,
  PricingTableContext,
  PricingTableContextProvider,
} from './context/pricing-table-context';

import '@bbkPricingPage/pricing-table.scss';

// Regex pattern to extract pricingPageKey from /pricing/{key} or /geo/pricing/{key}
const PRICING_PAGE_KEY_PATTERN = /^(?:\/geo)?\/pricing\/([^/]+)/;

const buildPricingPageHostEventMetadata = ({
  pricingPageKey,
  sessionId,
  legacySiteId,
  legacyPricingPageId,
}: {
  pricingPageKey?: string;
  sessionId?: string;
  legacySiteId?: string;
  legacyPricingPageId?: string;
}) => ({
  pricingPageKey: pricingPageKey ?? '',
  urlDomain: typeof window !== 'undefined' ? window.location.origin : '',
  ...(sessionId ? { sessionId } : {}),
  ...(legacySiteId ? { legacySiteId } : {}),
  ...(legacyPricingPageId ? { legacyPricingPageId } : {}),
});

const PricingTable: React.FC = () => {
  const {
    pricingTable,
    currency,
    setCurrency,
    frequency,
    priceVariantId,
    discounts,
    subscription,
    country,
    defaultCurrency,
    isPreview,
    isFullPreview,
    showCurrencyDropdown,
    urlCurrencyCode,
    executeEvents,
    visitor,
    showPrice,
    legacySiteId,
    legacyPricingPageId,
  } = useContext(PricingTableContext);

  const { pricingPageProps } = useContext(FunnelContext);

  const hostEventMetadata = useMemo(
    () =>
      buildPricingPageHostEventMetadata({
        pricingPageKey: pricingPageProps?.pricingPageKey,
        sessionId: pricingPageProps?.sessionId,
        legacySiteId,
        legacyPricingPageId,
      }),
    [
      pricingPageProps?.pricingPageKey,
      pricingPageProps?.sessionId,
      legacySiteId,
      legacyPricingPageId,
    ]
  );

  const pricingTableEventSent = useRef(false);

  useEffect(() => {
    if (!executeEvents || !visitor || pricingTableEventSent.current) return;

    const eventData = {
      app_id: pricingPageProps?.appId ?? '',
      source: EventSource.PRICING_PAGE,
      session: pricingPageProps?.sessionId ?? null,
      id: uuidv4(),
      name: 'pricing_table_loaded',
      event: 'pricing_table_loaded',
      properties: {},
      pricing_page_properties: {
        visitor_id: getOrCreateVisitorId(),
        pricing_page_id: pricingPageProps?.pricingPageId,
        pricing_page_ev_id: pricingPageProps?.pricingPageEVId,
      },
    } as EventObject;
    executeEvents({
      events: [eventData],
    });
    pricingTableEventSent.current = true;
  }, [executeEvents, visitor, pricingPageProps?.appId]);

  const [pageLoaded, setPageLoaded] = useState(false);
  const windowSize = useWindowSize();
  const containerRef = useRef<HTMLDivElement>(null);
  const [refDimensions, setRefDimensions] = useState<{
    height: number;
    width: number;
  } | null>(null);

  const pageHasBadge =
    pricingTable?.items.some(PricingTableHelper.hasBadge) ?? false;

  const hideSubscribedPlan =
    pricingTable?.paywall_config?.hide_subscribed_plan ?? false;

  const activeItemPrices = useMemo(() => {
    const result = PricingTableHelper.getActiveItemPrices(
      pricingTable?.items,
      currency,
      frequency as Frequency,
      priceVariantId,
      pricingTable?.grouped_by,
      hideSubscribedPlan,
      subscription
    );
    return result;
  }, [
    pricingTable?.items,
    pricingTable?.grouped_by,
    currency,
    frequency,
    priceVariantId,
    hideSubscribedPlan,
    subscription,
  ]);

  // send active item prices viewed event
  // we are going to keep track of last viewed item price ids and if it changes then we send this event
  const lastViewedItemPriceIds = useRef<string[]>([]);
  useEffect(() => {
    if (!executeEvents || !visitor) return;
    const currentViewedItemPriceIds = activeItemPrices.map(
      (item) => item.price?.billing_system_id
    );
    if (
      lastViewedItemPriceIds.current.join(',') ===
      currentViewedItemPriceIds.join(',')
    )
      return;
    lastViewedItemPriceIds.current = currentViewedItemPriceIds;

    const eventData = {
      app_id: pricingPageProps?.appId ?? '',
      source: EventSource.PRICING_PAGE,
      session: pricingPageProps?.sessionId ?? null,
      id: uuidv4(),
      name: 'pricing_items_viewed',
      event: 'pricing_items_viewed',
      properties: {},
      pricing_page_properties: {
        visitor_id: getOrCreateVisitorId(),
        billing_system_ids_displayed: currentViewedItemPriceIds,
        pricing_page_id: pricingPageProps?.pricingPageId,
        pricing_page_ev_id: pricingPageProps?.pricingPageEVId,
      },
    } as EventObject;
    executeEvents({
      events: [eventData],
    });
  }, [executeEvents, activeItemPrices]);

  const noOfPlans =
    activeItemPrices?.reduce(
      (acc, activeItemPrice) => acc + (activeItemPrice.price ? 1 : 0),
      0
    ) ?? 0;

  const currencies = useMemo(() => {
    const currencies =
      PricingTableHelper.getCurrenciesFromPricingTable(pricingTable);
    if (subscription?.currency) {
      return currencies.filter(
        (currency) => currency.currency_code === subscription?.currency
      );
    }
    return currencies;
  }, [pricingTable, subscription]);

  const getFirstAttachedCurrency = (currencies: Currency[]) =>
    currencies.find((c) => c.is_attached);

  const getCurrency = (currencies: Currency[], currencyCode?: string) => {
    let _currency = currencies.find((c) => c.currency_code === currencyCode);
    if (!_currency) {
      _currency = getFirstAttachedCurrency(currencies);
    }
    return _currency;
  };
  useEffect(() => {
    // URL currency param takes highest priority
    if (urlCurrencyCode) {
      const urlBased = getCurrency(currencies, urlCurrencyCode);
      if (urlBased) {
        setCurrency(urlBased);
        return;
      }
    }

    // Geo-resolved default currency from backend header (second priority)
    if (defaultCurrency) {
      const geoBased = getCurrency(currencies, defaultCurrency);
      if (geoBased) {
        setCurrency(geoBased);
        return;
      }
    }

    const countryBasedCurrency = country
      ? getCurrencyFromIso2CountryCode(country as Iso2CountryCode)
      : undefined;

    setCurrency((prevCurrency: Currency) => {
      let next: Currency | undefined;
      if (countryBasedCurrency) {
        next =
          getCurrency(currencies, countryBasedCurrency) ||
          getCurrency(currencies, prevCurrency?.currency_code);
      } else {
        next = getCurrency(currencies, prevCurrency?.currency_code);
      }
      if (
        next &&
        prevCurrency &&
        next.currency_code === prevCurrency.currency_code
      ) {
        return prevCurrency;
      }
      return next ?? prevCurrency;
    });
  }, [currencies, setCurrency, country, defaultCurrency, urlCurrencyCode]);

  // Send PAGE_LOAD event when the page loads (only if inside an iframe)
  useEffect(() => {
    // Check if page is inside an iframe
    const isInIframe = window.self !== window.top;

    // When the page is loaded, inside an iframe, and host parameter is set, then emit an event to host
    if (isInIframe && !pageLoaded) {
      sendEvent(EventKeys.PAGE_LOAD, {
        pricingPage: pricingTable,
        ...hostEventMetadata,
      });
      setPageLoaded(true);
    }
  }, [pricingPageProps?.host, pageLoaded, pricingTable, hostEventMetadata]);

  // DIMENSION height = header + table (.pricing-page sections only).
  useEffect(() => {
    const measureTarget = containerRef.current;
    if (!measureTarget) return;

    const getPricingSections = (root: HTMLElement): HTMLElement[] =>
      Array.from(root.children).filter(
        (el): el is HTMLElement =>
          el instanceof HTMLElement && el.classList.contains('pricing-page')
      );

    const updateDimensions = () => {
      const funnelApp = document.getElementById('FunnelApp');
      const contentRoot = funnelApp ?? measureTarget;
      const sections = funnelApp
        ? getPricingSections(funnelApp)
        : [measureTarget];
      const height =
        sections.length > 0
          ? sections.reduce((sum, el) => sum + el.offsetHeight, 0)
          : contentRoot.scrollHeight;

      setRefDimensions({
        height,
        width: contentRoot.scrollWidth,
      });
    };

    const resizeObserver = new ResizeObserver(updateDimensions);

    const observeTargets = () => {
      resizeObserver.disconnect();
      resizeObserver.observe(measureTarget);
      const funnelApp = document.getElementById('FunnelApp');
      if (funnelApp && funnelApp !== measureTarget) {
        for (const section of getPricingSections(funnelApp)) {
          resizeObserver.observe(section);
        }
      }
      updateDimensions();
    };

    observeTargets();

    const funnelApp = document.getElementById('FunnelApp');
    let mutationObserver: MutationObserver | undefined;
    if (funnelApp && funnelApp !== measureTarget) {
      mutationObserver = new MutationObserver(observeTargets);
      mutationObserver.observe(funnelApp, { childList: true });
    }

    return () => {
      resizeObserver.disconnect();
      mutationObserver?.disconnect();
    };
  }, []);

  // Send DIMENSION event when dimensions are available and page is loaded
  useEffect(() => {
    if (refDimensions && pageLoaded) {
      sendEvent(EventKeys.DIMENSION, {
        ...hostEventMetadata,
        window: {
          height: windowSize.height,
          width: windowSize.width,
        },
        scroll: {
          height: refDimensions.height,
          width: refDimensions.width,
        },
      });
    }
  }, [refDimensions, windowSize, pageLoaded, hostEventMetadata]);

  const theme = useTheme() as EmotionTheme;

  const child1Ref = useRef<HTMLDivElement>(null);
  const child2Ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!child1Ref.current || !child2Ref.current) return;

    const updateWidth = () => {
      if (noOfPlans <= 3) return;
      const children = Array.from(child1Ref.current!.children) as HTMLElement[];
      const childElemWidth = children.reduce(
        (acc, child) => acc + child.offsetWidth,
        0
      );
      const gap = parseInt(getComputedStyle(child1Ref.current!).gap || '0');
      const totalWidth = childElemWidth + gap * (children.length - 1);
      child2Ref.current!.style.width = `${totalWidth}px`;
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(child1Ref.current);

    window.addEventListener('resize', updateWidth);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, [noOfPlans]);

  const showEmptyCardsBanner =
    noOfPlans <= 0 ||
    !currency ||
    (pricingTable?.grouped_by !== GroupedBy.item_frequency && !frequency);

  return (
    <div
      ref={containerRef}
      className="tw-w-full tw-@container"
      style={{ fontFamily: 'var(--pricing-page-font-family)' }}
    >
      <div className="!tw-bg-[var(--pricing-page-background-color)] tw-px-4 tw-pb-10 @sm:tw-px-6 @md:tw-px-12">
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-space-y-3 tw-py-4 @md:tw-flex-row @md:tw-space-x-2 @md:tw-space-y-0">
          {pricingTable?.grouped_by !== GroupedBy.item_frequency && (
            <PeriodSwitchBox />
          )}
          <CurrencyDropdown currencies={currencies} />
        </div>

        {showEmptyCardsBanner ? (
          <div className="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-rounded-pricingtable-border-radius tw-border tw-border-pricingtable-text-base/10 tw-bg-pricingtable-background tw-p-8 tw-backdrop-brightness-150">
            <h1 className="tw-my-2 tw-text-center !tw-text-3xl tw-font-bold tw-tracking-tight tw-text-pricingtable-text-base @sm:!tw-text-4xl">
              No Plans Available
            </h1>
            <p className="tw-my-2 tw-text-center tw-text-pricingtable-text-base/60 @sm:tw-text-xl">
              {isPreview && !isFullPreview
                ? 'Please add plans and prices via "Manage Product Catalog" and "Currency & Frequency" settings on the left customisation menu'
                : 'Please contact support for assistance'}
            </p>
          </div>
        ) : (
          <div className={cs('tw-flex tw-w-full tw-flex-col tw-items-center')}>
            <div
              ref={child1Ref}
              className={cs(
                'tw-group tw-mt-4 tw-flex tw-w-full tw-flex-col tw-flex-wrap tw-items-center tw-justify-center tw-justify-items-center tw-gap-2 tw-px-0',
                noOfPlans <= 3 &&
                  '@md:tw-flex-row @md:tw-items-stretch @md:tw-gap-0 @lg:tw-gap-2',
                noOfPlans === 4 &&
                  '@lg:tw-flex-row @lg:tw-items-stretch @lg:tw-gap-0 @xl:tw-gap-2',
                noOfPlans === 5 &&
                  '@xl:tw-flex-row @xl:tw-items-stretch @xl:tw-gap-0 @2xl:tw-gap-2',
                noOfPlans >= 6 && 'tw-flex-col tw-items-stretch'
              )}
            >
              {activeItemPrices.map(
                (
                  { item, price, isSubscribedItem, isSubscribedPrice },
                  index
                ) => {
                  const priceFrequency =
                    pricingTable?.grouped_by === GroupedBy.item_frequency
                      ? getApplicableFrequency(
                          price,
                          subscription,
                          pricingTable.frequencies,
                          pricingTable.paywall_config?.hide_frequency_switch
                        )
                      : frequency;
                  if (!priceFrequency) return null;
                  return (
                    <PricingCard
                      key={
                        price.billing_system_id +
                        price.period +
                        price.period_unit
                      }
                      item={item}
                      price={price}
                      noOfPlans={noOfPlans}
                      frequency={priceFrequency}
                      pageHasBadge={pageHasBadge}
                      isFirst={index === 0}
                      isLast={index === activeItemPrices.length - 1}
                      applicableDiscounts={
                        discounts?.filter(
                          (discount) =>
                            discount.item_price_id === price.billing_system_id
                        ) ?? []
                      }
                      showPrice={showPrice}
                    />
                  );
                }
              )}
            </div>
            <div
              ref={child2Ref}
              className={cs('tw-max-w-full', noOfPlans <= 3 && 'tw-w-[72rem]')}
            >
              <PlanCustomisation activeItemPrices={activeItemPrices} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const PricingTableContextWrapper = ({
  pricingTable,
  subscription,
  discounts,
  country,
  defaultCurrency,
  executeEvents,
}: {
  pricingTable: PricingTableType;
  subscription?: Subscription;
  discounts?: Discount[];
  country?: string;
  defaultCurrency?: string;
  executeEvents: ExecuteEventsHandler;
}) => {
  const { pricingPageProps } = useContext(FunnelContext);

  // Get URL parameters and path
  const urlParams = useMemo(() => {
    if (typeof window === 'undefined')
      return {
        host: undefined,
        pricingPageKey: undefined,
        sessionId: undefined,
        currency: undefined,
        periodUnit: undefined,
        period: undefined,
        showCurrencyDropdown: true,
      };
    const params = new URLSearchParams(window.location.search);
    const pathMatch = window.location.pathname.match(PRICING_PAGE_KEY_PATTERN);
    const pricingPageKey = pathMatch ? pathMatch[1] : undefined;
    const periodRaw = params.get('period');
    const periodParsed = periodRaw ? parseInt(periodRaw, 10) : NaN;

    return {
      host: params.get('host') || undefined,
      pricingPageKey,
      sessionId: params.get('sessionId') || undefined,
      currency: params.get('currency') || undefined,
      periodUnit: params.get('periodUnit') || undefined,
      period: !isNaN(periodParsed) ? periodParsed : undefined,
      showCurrencyDropdown: params.get('showCurrencyDropdown') !== 'false',
    };
  }, []);

  return (
    <PricingTableContextProvider
      pricingTable={pricingTable}
      subscription={subscription}
      discounts={discounts}
      country={country}
      defaultCurrency={defaultCurrency}
      host={urlParams.host}
      urlCurrency={urlParams.currency}
      urlPeriodUnit={urlParams.periodUnit}
      urlPeriod={urlParams.period}
      priceVariantId={pricingPageProps?.priceVariantId}
      showCurrencyDropdown={urlParams.showCurrencyDropdown}
      executeEvents={executeEvents}
    >
      <EqualHeightContextProvider>
        <PricingTable />
      </EqualHeightContextProvider>
    </PricingTableContextProvider>
  );
};

export default PricingTableContextWrapper;
